/**
 * Swappable source for milestone-latch events.
 *
 * On-chain, `latch_milestone` emits a `msg!("koth_latch_milestone: ...")`
 * line. The bridge hook `useFlywheelOnChainBridge` subscribes to program
 * logs, parses matching lines via `parseLatchLog`, and calls
 * `emitOnchain(...)`. The DevConsole's TokenStubs section uses
 * `emitStub(...)` to fire synthetic crossings without touching chain.
 *
 * Both paths fan out to the same listener set so consumers (toast + log
 * panel) read uniformly.
 */
import { create } from "zustand";

export interface MilestoneCrossing {
  /** Monotonic id assigned by the emitter. */
  id: number;
  /** Posted mcap in lamports that triggered the crossing. */
  postedMcapLamports: number;
  /** Threshold the cross cleared. */
  thresholdLamports: number;
  /** Total lamports paid out across the top-K. */
  payoutLamports: number;
  /** Per-winner pubkeys (base58). Empty if not known yet — the log line
   *  does not carry winner identities, so on-chain crossings ship empty
   *  here until a leaderboard fetch is wired keyed on the cross's slot. */
  winners: string[];
  ts: number;
  source: "stub" | "onchain";
}

export type MilestoneListener = (e: MilestoneCrossing) => void;

export interface MilestoneEventSource {
  subscribe(cb: MilestoneListener): () => void;
  /** Dev hook — fires a synthetic stub crossing. */
  emitStub(e: Omit<MilestoneCrossing, "id" | "ts" | "source">): void;
}

class SwappableMilestoneEventSource implements MilestoneEventSource {
  private readonly listeners = new Set<MilestoneListener>();
  private nextId = 1;

  subscribe(cb: MilestoneListener): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  emitStub(partial: Omit<MilestoneCrossing, "id" | "ts" | "source">): void {
    this.emit(partial, "stub");
  }

  /** Bridge calls this when a real on-chain crossing log is observed. */
  emitOnchain(partial: Omit<MilestoneCrossing, "id" | "ts" | "source">): void {
    this.emit(partial, "onchain");
  }

  private emit(
    partial: Omit<MilestoneCrossing, "id" | "ts" | "source">,
    source: "stub" | "onchain",
  ): void {
    const e: MilestoneCrossing = {
      ...partial,
      id: this.nextId++,
      ts: Date.now(),
      source,
    };
    for (const fn of this.listeners) fn(e);
  }
}

export const milestoneEvents = new SwappableMilestoneEventSource();

/**
 * Parses a single Solana log line from the program. Returns null for
 * anything that isn't a milestone latch. The format mirrors the `msg!`
 * call in `program/.../latch_milestone.rs`:
 *
 *   "Program log: koth_latch_milestone: posted_mcap=N peak=N next=N k=N payout=N slot=N"
 *
 * Both the bare and the "Program log: " prefixed forms are accepted because
 * RPC `onLogs` delivers the prefixed form while local test harnesses
 * sometimes pass the bare msg! body.
 */
export interface ParsedLatchLog {
  postedMcapLamports: number;
  peakLamports: number;
  /** Pre-cross threshold; the new "next" is computed by the program. */
  nextThresholdLamports: number;
  k: number;
  payoutLamports: number;
  slot: number;
}

const LATCH_LOG_RE =
  /koth_latch_milestone: posted_mcap=(\d+) peak=(\d+) next=(\d+) k=(\d+) payout=(\d+) slot=(\d+)/;

export function parseLatchLog(line: string): ParsedLatchLog | null {
  const m = LATCH_LOG_RE.exec(line);
  if (!m) return null;
  return {
    postedMcapLamports: Number(m[1]),
    peakLamports: Number(m[2]),
    nextThresholdLamports: Number(m[3]),
    k: Number(m[4]),
    payoutLamports: Number(m[5]),
    slot: Number(m[6]),
  };
}

/**
 * Capped in-memory log of recent crossings. The TokenMcapPanel renders
 * the last few rows; MilestoneToast renders the most recent.
 *
 * The bridge from `milestoneEvents.subscribe` to this store is installed
 * once at app start (see `installMilestoneLogBridge`) so the same event
 * fans out to both toast and log.
 */
const LOG_CAP = 25;

interface MilestoneLog {
  entries: MilestoneCrossing[];
  push: (e: MilestoneCrossing) => void;
  clear: () => void;
}

export const useMilestoneLog = create<MilestoneLog>((set) => ({
  entries: [],
  push: (e) =>
    set((s) => {
      const next = [e, ...s.entries];
      if (next.length > LOG_CAP) next.length = LOG_CAP;
      return { entries: next };
    }),
  clear: () => set({ entries: [] }),
}));

let bridgeInstalled = false;
export function installMilestoneLogBridge(): void {
  if (bridgeInstalled) return;
  bridgeInstalled = true;
  milestoneEvents.subscribe((e) => {
    useMilestoneLog.getState().push(e);
  });
}
